// IsomorphicService per ADR 010 — pure, read-only. The single buyer's-premium
// calculator: admin's editor preview, bidder's bid-total preview and the invoice
// pricing engine (BL-2032) all price through it, in Money (ADR 060 — Money Type).

import { Money } from '../money/money';
import type { Rate } from '../rate/rate';
import type { PremiumSchedule, PremiumTier } from './schedule';

/**
 * Applies the schedule's floor and cap, once, after the tiers.
 *
 * The floor applies only when the price is above zero (a free lot never incurs
 * a minimum fee). Inversion guard: when an operator has saved `minFee > maxFee`,
 * the effective floor is clamped to the cap, so the cap — which exists to
 * protect the buyer — is never breached.
 */
function applyBounds(premium: Money, price: Money, schedule: PremiumSchedule): Money {
  const { minFee, maxFee } = schedule;
  let result = premium;
  if (maxFee != null && result.compare(maxFee) > 0) result = maxFee;
  if (minFee != null && price.compare(zeroOf(price)) > 0) {
    const floor = maxFee != null && minFee.compare(maxFee) > 0 ? maxFee : minFee;
    if (result.compare(floor) < 0) result = floor;
  }
  return result;
}

function zeroOf(m: Money): Money {
  return Money.of('0', m.currency);
}

/** Tiers ascending by threshold, the unbounded tier (`null`) last. */
function sortTiers(tiers: readonly PremiumTier[]): PremiumTier[] {
  return [...tiers].sort((a, b) => {
    if (a.threshold == null) return b.threshold == null ? 0 : 1;
    if (b.threshold == null) return -1;
    return a.threshold.compare(b.threshold);
  });
}

/**
 * The tier a price lands in: the lowest tier whose threshold covers it
 * (thresholds are inclusive), else the top tier — which defends a hand-built
 * all-finite schedule whose ceiling sits below the price.
 */
function landingTier(price: Money, tiers: readonly PremiumTier[]): PremiumTier {
  const sorted = sortTiers(tiers);
  return sorted.find((t) => t.threshold == null || price.compare(t.threshold) <= 0) ?? sorted[sorted.length - 1];
}

/**
 * Marginal: each tier's rate applies only to its slice of the price, each slice
 * rounded to the minor unit before summing. Price above the last finite
 * threshold is not charged (there is no tier covering it).
 */
function marginalPremium(price: Money, tiers: readonly PremiumTier[]): Money {
  let total = zeroOf(price);
  let floor = zeroOf(price);
  for (const tier of sortTiers(tiers)) {
    const ceiling = tier.threshold == null || price.compare(tier.threshold) < 0 ? price : tier.threshold;
    const slice = ceiling.minus(floor);
    if (slice.compare(zeroOf(price)) > 0) total = total.plus(slice.times(tier.rate));
    if (tier.threshold == null || price.compare(tier.threshold) <= 0) break;
    floor = tier.threshold;
  }
  return total;
}

/**
 * The buyer's premium on one lot's price.
 *
 * Flat charges its rate on the whole price. Tiered combines its tiers by
 * `combineMode`: Marginal (each tier on its slice) or Bracket (the whole price at
 * the one tier it lands in, thresholds inclusive). Floor and cap apply once,
 * after the tiers. Products round half-up to the currency's minor unit.
 *
 * @param price - The lot's price (hammer, or a re-priced hammer per CR10 (a)).
 * @param schedule - The resolved schedule, or `null` when the lot carries none.
 * @returns The premium, in the price's currency; zero for no schedule or a
 *   price of zero or less.
 * @throws CurrencyMismatchError when the schedule's dollar figures are in
 *   another currency.
 */
export function calculateBuyersPremium(price: Money, schedule: PremiumSchedule | null): Money {
  if (schedule == null || price.compare(zeroOf(price)) <= 0) return zeroOf(price);
  if (schedule.type === 'Flat') return applyBounds(price.times(schedule.rate), price, schedule);
  if (schedule.tiers.length === 0) return zeroOf(price);
  const raw =
    schedule.combineMode === 'Bracket'
      ? price.times(landingTier(price, schedule.tiers).rate)
      : marginalPremium(price, schedule.tiers);
  return applyBounds(raw, price, schedule);
}

/**
 * The headline rate at a price: the flat rate, or the rate of the tier the price
 * lands in (the Bracket rate, and the marginal rate at that price). The one
 * place "which tier does this price land in" is decided (ADR 010 § 5).
 *
 * @param price - The price to read the rate at.
 * @param schedule - The resolved schedule, or `null`.
 * @returns The rate, or `null` when the schedule charges nothing.
 */
export function premiumRateAt(price: Money, schedule: PremiumSchedule | null): Rate | null {
  if (schedule == null) return null;
  if (schedule.type === 'Flat') return schedule.rate;
  if (schedule.tiers.length === 0) return null;
  return landingTier(price, schedule.tiers).rate;
}
